/* eslint-disable react/display-name */
import React, { useMemo, useState } from 'react'
import { useQuery, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import { restaurants } from '../apollo'
import { WEB_APP_URL, embedUrl } from '../utils/storeLinks'
import {
  Box, Container, Typography, Paper, InputBase,
  Button, CircularProgress, Chip, Snackbar, IconButton, Tooltip
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import StorefrontIcon from '@mui/icons-material/Storefront'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CodeIcon from '@mui/icons-material/Code'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import RefreshIcon from '@mui/icons-material/Refresh'
import LinkIcon from '@mui/icons-material/Link'

const GET_RESTAURANTS = gql`${restaurants}`

// Each menu's link key, made by the backend (utils/menuLinks.js): the menus the
// restaurant page shows, then Catering. app.rushmyfood.com/<restaurant>/<key>
// opens the pinned restaurant on that menu.
const GET_MENU_LINKS = gql`
  query EmbedMenuLinks {
    embedMenuLinks { restaurantId menus { menuId name key } }
  }
`

// Short link: app.rushmyfood.com/sagesfusionkitchen. The web app asks the
// backend (embedSlug) which restaurant the key means, then opens the full
// embed URL. Must match the backend's squash.
const squashSlug = slug => String(slug || '').toLowerCase().replace(/[^a-z0-9]/g, '')

// If two slugs squash to the same key, the backend resolves it to the older
// restaurant, so every other one keeps its full slug (an exact match wins).
const shortUrl = (slug, ownerOfKey) => {
  const key = squashSlug(slug)
  return ownerOfKey[key] === slug && key
    ? `${WEB_APP_URL}/${key}`
    : `${WEB_APP_URL}/${encodeURIComponent(slug)}`
}

// The snippet a partner pastes into their own site. The allow list is required:
// payment for the card fields, geolocation for address lookup. No sandbox attribute.
const embedSnippet = (slug, name) => `<iframe
  src="${embedUrl(slug)}"
  title="Order from ${name}"
  width="100%"
  height="900"
  style="border:0;"
  allow="payment *; geolocation *"
></iframe>`

const copyToClipboard = async text => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (e) {
    // Clipboard API needs a secure context — fall back to a hidden textarea.
    try {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      const ok = document.execCommand('copy')
      document.body.removeChild(ta)
      return ok
    } catch (err) {
      return false
    }
  }
}

const EmbedLinks = props => {
  const [searchQuery, setSearchQuery] = useState('')
  const [snack, setSnack] = useState(null)

  const { data, loading, refetch } = useQuery(GET_RESTAURANTS, { fetchPolicy: 'network-only' })
  const { data: menuData, refetch: refetchMenus } = useQuery(GET_MENU_LINKS, { fetchPolicy: 'network-only', errorPolicy: 'all' })
  const menusOf = useMemo(() => {
    const out = {}
    ;(menuData?.embedMenuLinks ?? []).forEach(r => { out[String(r.restaurantId)] = r.menus || [] })
    return out
  }, [menuData])

  const allRestaurants = data?.restaurants ?? []
  const withSlug = allRestaurants.filter(r => !!r.slug)
  const missingSlug = allRestaurants.length - withSlug.length

  // First restaurant (by id) to claim each squashed key, mirroring the backend.
  const ownerOfKey = useMemo(() => {
    const out = {}
    ;[...withSlug]
      .sort((a, b) => Number(a._id) - Number(b._id))
      .forEach(r => {
        const k = squashSlug(r.slug)
        if (!(k in out)) out[k] = r.slug
      })
    return out
  }, [withSlug])

  const filtered = useMemo(() => {
    if (searchQuery.length < 2) return withSlug
    const q = searchQuery.toLowerCase()
    return withSlug.filter(r =>
      (r.name || '').toLowerCase().includes(q) ||
      (r.slug || '').toLowerCase().includes(q) ||
      (r.address || '').toLowerCase().includes(q) ||
      (r.owner?.email || '').toLowerCase().includes(q)
    )
  }, [withSlug, searchQuery])

  const handleCopy = async (text, label) => {
    const ok = await copyToClipboard(text)
    setSnack(ok ? `${label} copied` : 'Could not copy. Select the link and copy it manually.')
  }

  return (
    <Container maxWidth="xl" sx={{ pt: 3, pb: 4 }}>
      {/* Header row */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: '#0ea5e9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LinkIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.4rem', color: '#0f172a', lineHeight: 1.2 }}>Embed Links</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>
              A private ordering link per restaurant. Customers who open it see that restaurant only.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', px: 2, py: 0.8 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#1d4ed8' }}>{loading ? '…' : withSlug.length}</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#3b82f6', fontWeight: 600 }}>Links</Typography>
          </Box>
          {missingSlug > 0 && (
            <Tooltip title="These restaurants have no slug yet, so they cannot get an embed link">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', px: 2, py: 0.8 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#d97706' }}>{missingSlug}</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#d97706', fontWeight: 600 }}>No slug</Typography>
              </Box>
            </Tooltip>
          )}
          <IconButton onClick={() => { refetch(); refetchMenus() }} size="small" sx={{ ml: 0.5, bgcolor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '10px', '&:hover': { bgcolor: '#e2e8f0' } }}>
            <RefreshIcon sx={{ fontSize: 18, color: '#64748b' }} />
          </IconButton>
        </Box>
      </Box>

      {/* What these links do */}
      <Paper elevation={0} sx={{ borderRadius: '14px', border: '1px solid #bae6fd', bgcolor: '#f0f9ff', px: 2.5, py: 2, mb: 3 }}>
        <Typography sx={{ fontSize: '0.83rem', fontWeight: 700, color: '#075985', mb: 0.75 }}>
          How an embed link behaves
        </Typography>
        <Typography sx={{ fontSize: '0.8rem', color: '#0c4a6e', lineHeight: 1.6 }}>
          Opening one of these links pins the web app to a single restaurant. Home, search and every other
          restaurant page bounce straight back to the pinned menu, so the customer never sees the wider
          marketplace. Checkout, payment, order tracking and login all keep working, and My Orders shows only
          that restaurant&apos;s orders. Use <b>Copy link</b> for the short link (a button, QR code or text message), and <b>Copy embed code</b>
          &nbsp;when the partner wants the menu inside a page on their own site. Each menu also has its own
          link under <b>Menu links</b>: it opens the same pinned page on that menu, for a Catering or Breakfast
          button on the partner&apos;s site.
        </Typography>
      </Paper>

      {/* Search bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', px: 2, py: 1, mb: 3, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
        <InputBase
          placeholder="Search by name, slug, address or vendor email..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          sx={{ fontSize: '0.875rem', flex: 1, color: '#475569' }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <StorefrontIcon sx={{ fontSize: 52, color: '#e2e8f0', mb: 1 }} />
          <Typography sx={{ color: '#94a3b8' }}>No restaurants found</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {filtered.map(r => {
            const url = shortUrl(r.slug, ownerOfKey)
            const menus = menusOf[String(r._id)] || []
            return (
              <Paper
                key={r._id}
                elevation={0}
                sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', bgcolor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', p: 2, transition: 'all 0.18s', '&:hover': { borderColor: '#cbd5e1', boxShadow: '0 6px 20px rgba(0,0,0,0.08)' } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  {/* Thumbnail */}
                  <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: '#f1f5f9', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {r.image
                      ? <img src={r.image} alt={r.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <StorefrontIcon sx={{ fontSize: 22, color: '#cbd5e1' }} />
                    }
                  </Box>

                  {/* Name + slug */}
                  <Box sx={{ minWidth: 200, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a', lineHeight: 1.3 }}>{r.name}</Typography>
                      <Chip
                        icon={<CheckCircleIcon sx={{ fontSize: '13px !important', color: `${r.isActive ? '#16a34a' : '#d97706'} !important` }} />}
                        label={r.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{ bgcolor: r.isActive ? '#dcfce7' : '#fef3c7', color: r.isActive ? '#16a34a' : '#d97706', fontWeight: 700, fontSize: '0.68rem', height: 20, '& .MuiChip-icon': { ml: '5px' } }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontFamily: 'monospace', mt: 0.2 }}>{r.slug}</Typography>
                  </Box>

                  {/* Actions */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      startIcon={<ContentCopyIcon sx={{ fontSize: '16px !important' }} />}
                      onClick={() => handleCopy(url, 'Link')}
                      size="small"
                      sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', color: '#fff', bgcolor: '#0ea5e9', borderRadius: '10px', px: 1.6, py: 0.6, boxShadow: 'none', '&:hover': { bgcolor: '#0284c7', boxShadow: 'none' } }}
                    >
                      Copy link
                    </Button>
                    <Button
                      startIcon={<CodeIcon sx={{ fontSize: '16px !important' }} />}
                      onClick={() => handleCopy(embedSnippet(r.slug, r.name), 'Embed code')}
                      size="small"
                      sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', color: '#475569', bgcolor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '10px', px: 1.6, py: 0.6, '&:hover': { bgcolor: '#e2e8f0' } }}
                    >
                      Copy embed code
                    </Button>
                    <Tooltip title="Open in a new tab">
                      <IconButton
                        component="a"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        sx={{ bgcolor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '10px', '&:hover': { bgcolor: '#e2e8f0' } }}
                      >
                        <OpenInNewIcon sx={{ fontSize: 17, color: '#64748b' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* The link itself, selectable for a manual copy */}
                <Box
                  sx={{ mt: 1.5, px: 1.5, py: 1, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', overflowX: 'auto' }}
                >
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.74rem', color: '#475569', whiteSpace: 'nowrap', userSelect: 'all' }}>
                    {url}
                  </Typography>
                </Box>

                {/* A link straight to each menu */}
                {menus.length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.75 }}>
                      Menu links
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
                      {menus.map(m => {
                        const menuUrl = `${url}/${m.key}`
                        return (
                          <Box key={m.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 1.5, pr: 0.5, py: 0.6, border: '1px solid #e2e8f0', borderRadius: '10px', bgcolor: '#fff', minWidth: 0 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#0f172a', lineHeight: 1.35 }}>{m.name}</Typography>
                              {/* Phones show the path alone, so the menu part isn't cut off */}
                              <Typography noWrap sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#64748b', userSelect: 'all' }}>
                                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{WEB_APP_URL}</Box>
                                {menuUrl.slice(WEB_APP_URL.length)}
                              </Typography>
                            </Box>
                            <Tooltip title={`Copy ${m.name} link`}>
                              <IconButton size="small" onClick={() => handleCopy(menuUrl, `${m.name} link`)} aria-label={`Copy ${m.name} link`} sx={{ color: '#0ea5e9' }}>
                                <ContentCopyIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Open in a new tab">
                              <IconButton size="small" component="a" href={menuUrl} target="_blank" rel="noopener noreferrer" aria-label={`Open ${m.name} link`} sx={{ color: '#64748b' }}>
                                <OpenInNewIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )
                      })}
                    </Box>
                  </Box>
                )}
              </Paper>
            )
          })}
        </Box>
      )}

      <Snackbar
        open={!!snack}
        autoHideDuration={2500}
        onClose={() => setSnack(null)}
        message={snack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  )
}

export default withTranslation()(EmbedLinks)
